/**
 * Users Admin Service
 * Handles admin user management operations
 */

import api from './apiClient';

/**
 * Get all users with pagination (Admin only)
 * @param {Object} params - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.pageSize=20] - Items per page
 * @param {boolean} [params.includeDisabled=true] - Include disabled users
 * @param {string} [params.search] - Search term for filtering
 * @returns {Promise<Object>} Paginated users response
 */
export const getUsers = async ({ page = 1, pageSize = 20, includeDisabled = true, search } = {}) => {
  const params = {
    page,
    pageSize,
    includeDisabled,
  };
  
  if (search) {
    params.search = search;
  }

  return await api.get('/auth/admin/users', params);
};

/**
 * Get a specific user by ID (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUser = async (userId) => {
  return await api.get(`/auth/admin/users/${userId}`);
};

/**
 * Set user enabled/disabled status (Admin only)
 * @param {string} userId - User ID
 * @param {boolean} isDisabled - Whether to disable the user
 * @returns {Promise<Object>} Updated user data
 */
export const setUserStatus = async (userId, isDisabled) => {
  return await api.post('/auth/admin/users/set-status', {
    userId,
    isDisabled,
  });
};

/**
 * Change user password (Admin only, non-admin users)
 * @param {string} userId - User ID
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Response
 */
export const changeUserPassword = async (userId, newPassword, confirmPassword) => {
  return await api.post('/auth/admin/users/change-password', {
    userId,
    newPassword,
    confirmPassword,
  }, { suppressNotification: true });
};

/**
 * Create a new user (Admin only)
 * @param {Object} userData - User creation data
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 * @param {string} [userData.firstName] - First name
 * @param {string} [userData.lastName] - Last name
 * @param {string} [userData.preferredLanguage] - Language code
 * @param {string} [userData.role] - Role to assign
 * @returns {Promise<Object>} Created user data
 */
export const createUser = async (userData) => {
  return await api.post('/auth/admin/users', userData);
};

/**
 * Require user to change password on next login (Admin only)
 * Used for suspected breaches or security incidents (NIST-compliant)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response
 */
export const requirePasswordChange = async (userId) => {
  return await api.post(`/auth/admin/users/${userId}/require-password-change`, {}, {
    successMessage: 'User must change password on next login',
  });
};

/**
 * Revoke all sessions for a user (Admin only)
 * Forces logout on all devices
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response with revokedCount
 */
export const revokeUserSessions = async (userId) => {
  return await api.post(`/auth/admin/users/${userId}/revoke-sessions`, {}, {
    successMessage: 'All user sessions revoked',
  });
};

/**
 * Revoke all trusted devices for a user (Admin only)
 * Forces 2FA on next login from all devices
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response with revokedCount
 */
export const revokeUserTrustedDevices = async (userId) => {
  return await api.post(`/auth/admin/users/${userId}/revoke-trusted-devices`, {}, {
    successMessage: 'All trusted devices revoked',
  });
};

/**
 * Change user's role (Admin only, non-admin users only)
 * Cannot change admin users or assign admin role
 * @param {string} userId - User ID
 * @param {string} newRole - New role to assign
 * @returns {Promise<Object>} Updated user data
 */
export const changeUserRole = async (userId, newRole) => {
  return await api.post('/auth/admin/users/change-role', {
    userId,
    newRole,
  }, {
    successMessage: 'User role changed successfully',
  });
};

// ======================
// Pending Registration Management
// ======================

/**
 * Get pending user registrations (Admin only)
 * @param {Object} params - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.pageSize=20] - Items per page
 * @param {string} [params.search] - Search term
 * @param {boolean} [params.emailConfirmed] - Filter by email confirmation status
 * @returns {Promise<Object>} Paginated pending users response
 */
export const getPendingUsers = async ({ page = 1, pageSize = 20, search, emailConfirmed } = {}) => {
  const params = { page, pageSize };
  
  if (search) {
    params.search = search;
  }
  if (emailConfirmed !== undefined) {
    params.emailConfirmed = emailConfirmed;
  }

  return await api.get('/auth/admin/users/pending', params);
};

/**
 * Approve a pending user registration (Admin only)
 * Sends approval email with "Set Your Password" link
 * @param {string} userId - User ID
 * @param {string} [welcomeMessage] - Optional welcome message
 * @returns {Promise<Object>} Approval response
 */
export const approveUser = async (userId, welcomeMessage) => {
  const body = welcomeMessage ? { welcomeMessage } : {};
  return await api.post(`/auth/admin/users/${userId}/approve`, body, {
    successMessage: 'User approved successfully',
  });
};

/**
 * Reject a pending user registration (Admin only)
 * The user will be deleted from the database and notified via email.
 * @param {string} userId - User ID
 * @param {Object} options
 * @param {string} [options.reason] - Optional rejection reason
 * @returns {Promise<Object>} Rejection response
 */
export const rejectUser = async (userId, { reason } = {}) => {
  return await api.post(`/auth/admin/users/${userId}/reject`, { reason, sendEmail: true }, {
    successMessage: 'User registration rejected',
  });
};

/**
 * Get registration survey responses for a user (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Survey responses
 */
export const getUserRegistrationSurveyResponses = async (userId) => {
  return await api.get(`/auth/admin/users/${userId}/registration-survey-responses`);
};

const usersService = {
  getUsers,
  getUser,
  setUserStatus,
  changeUserPassword,
  createUser,
  requirePasswordChange,
  revokeUserSessions,
  revokeUserTrustedDevices,
  changeUserRole,
  // Pending registrations
  getPendingUsers,
  approveUser,
  rejectUser,
  getUserRegistrationSurveyResponses,
};

export default usersService;
